import { useEffect, useState } from 'react';
import { Plus, Star } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { maskCPF } from '../../../shared/utils/validators';
import { mensagemDeErro } from '../api';
import { BankAccount, BankAccountPayload, Owner } from '../api/cadastro';
import {
  useBancos, useContaPrincipal, useContas, useCriarConta, useEncerrarConta, useRejeitarConta, useValidarConta,
} from '../hooks/cadastro';
import { ACCOUNT_TYPE_OPTIONS, BANK_ACCOUNT_STATUS_LABELS, formatDate, labelOf, withDigit } from './format';
import { PessoasModal } from './PessoasModal';

interface Props {
  owner: Owner;
  canCreate: boolean;
  canEdit?: boolean;
  canValidate?: boolean;
}

/**
 * Contas bancárias historizadas (US-GP-004). A conta vem mascarada para
 * quem só consulta; toda conta nova fica pendente de validação.
 */
export function DadosBancariosTab({ owner, canCreate, canEdit = false, canValidate = false }: Props) {
  const toast = useToast();
  const contas = useContas(owner);
  const validar = useValidarConta();
  const rejeitar = useRejeitarConta();
  const principal = useContaPrincipal();
  const encerrar = useEncerrarConta();
  const [creating, setCreating] = useState(false);
  const [deciding, setDeciding] = useState<{ account: BankAccount; mode: 'validar' | 'rejeitar' } | null>(null);
  const [text, setText] = useState('');

  async function run(action: () => Promise<unknown>, ok: string, fail: string) {
    try {
      await action();
      toast.success(ok);
    } catch (err) {
      toast.error(mensagemDeErro(err, fail));
    }
  }

  async function decidir() {
    if (!deciding) return;
    const { account, mode } = deciding;
    await run(async () => {
      if (mode === 'validar') await validar.mutateAsync({ id: account.id, note: text.trim() || undefined });
      else await rejeitar.mutateAsync({ id: account.id, reason: text.trim() });
      setDeciding(null);
    }, mode === 'validar' ? 'Conta validada.' : 'Conta rejeitada.', 'Não foi possível concluir a validação.');
  }

  const lista = contas.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {owner.kind === 'me'
            ? 'Uma conta nova passa a ser usada nos pagamentos depois da validação do Departamento Pessoal.'
            : 'A conta principal é a usada na folha. Contas encerradas ficam só para consulta.'}
        </p>
        {canCreate && (
          <Button size="sm" onClick={() => setCreating(true)}><Plus className="mr-1 h-4 w-4" /> Nova conta</Button>
        )}
      </div>

      {contas.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : lista.length === 0 ? (
        <EmptyState size="compact" title="Nenhuma conta bancária" />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {lista.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-3 text-sm">
              <div>
                <p className="font-medium text-gray-900">
                  {c.bankCode ? `${c.bankCode} · ` : ''}{c.bankName}
                  {c.isPrimary && <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700"><Star className="h-3.5 w-3.5" /> Principal</span>}
                </p>
                <p className="text-gray-700">
                  Agência {withDigit(c.agency, c.agencyDigit)} · Conta {c.masked ? c.account : withDigit(c.account, c.accountDigit)} ·{' '}
                  {labelOf(ACCOUNT_TYPE_OPTIONS, c.accountType)}
                  {c.pixKey ? ` · PIX ${c.pixKey}` : ''}
                </p>
                <p className="text-xs text-gray-500">
                  {BANK_ACCOUNT_STATUS_LABELS[c.status]} · desde {formatDate(c.validFrom)}
                  {c.validTo ? ` até ${formatDate(c.validTo)}` : ''} · titular {c.holderName}
                  {c.holderDivergent && <span className="ml-1 font-medium text-red-600">(CPF do titular diferente do colaborador)</span>}
                  {c.rejectionReason ? ` · Rejeitada: ${c.rejectionReason}` : ''}
                  {c.validationNote ? ` · Observação: ${c.validationNote}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {canValidate && c.status === 'pendente_validacao' && (
                  <>
                    <Button size="sm" onClick={() => { setText(''); setDeciding({ account: c, mode: 'validar' }); }}>Validar</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setText(''); setDeciding({ account: c, mode: 'rejeitar' }); }}>Rejeitar</Button>
                  </>
                )}
                {canEdit && c.status === 'validada' && !c.isPrimary && (
                  <Button size="sm" variant="secondary" onClick={() => run(() => principal.mutateAsync(c.id), 'Conta principal alterada.', 'Não foi possível alterar a principal.')}>
                    Tornar principal
                  </Button>
                )}
                {canEdit && c.status !== 'encerrada' && !c.isPrimary && (
                  <Button size="sm" variant="ghost" onClick={() => run(() => encerrar.mutateAsync(c.id), 'Conta encerrada.', 'Não foi possível encerrar a conta.')}>
                    Encerrar
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <BankAccountModal owner={owner} open={creating} onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); toast.success('Conta cadastrada e enviada para validação.'); }} />

      <PessoasModal
        open={deciding !== null}
        title={deciding?.mode === 'validar' ? 'Validar conta' : 'Rejeitar conta'}
        description={
          deciding?.mode === 'validar' && deciding.account.holderDivergent
            ? 'O titular tem outro CPF: registre a justificativa da exceção.'
            : undefined
        }
        submitLabel={deciding?.mode === 'validar' ? 'Validar' : 'Rejeitar'}
        submitVariant={deciding?.mode === 'rejeitar' ? 'danger' : 'primary'}
        canSubmit={deciding?.mode === 'validar' ? !deciding.account.holderDivergent || text.trim().length > 0 : text.trim().length > 0}
        isSubmitting={validar.isPending || rejeitar.isPending}
        onClose={() => setDeciding(null)}
        onSubmit={decidir}
      >
        <Textarea
          label={deciding?.mode === 'validar' ? 'Observação / justificativa' : 'Motivo'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </PessoasModal>
    </div>
  );
}

const EMPTY: BankAccountPayload = { bankCode: '', agency: '', account: '', accountType: 'corrente' };

function BankAccountModal({ owner, open, onClose, onSaved }: { owner: Owner; open: boolean; onClose: () => void; onSaved: () => void }) {
  const bancos = useBancos(owner, open);
  const criar = useCriarConta(owner);
  const [value, setValue] = useState<BankAccountPayload>(EMPTY);
  const [outroTitular, setOutroTitular] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValue(EMPTY);
    setOutroTitular(false);
    setError(null);
  }, [open]);

  async function submit() {
    if (!value.bankCode || !value.agency || !value.account) return setError('Informe banco, agência e conta.');
    try {
      await criar.mutateAsync({
        ...value,
        agencyDigit: value.agencyDigit || undefined,
        accountDigit: value.accountDigit || undefined,
        pixKey: value.pixKey || undefined,
        holderName: outroTitular ? value.holderName : undefined,
        holderCpf: outroTitular ? value.holderCpf : undefined,
      });
      onSaved();
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível cadastrar a conta.'));
    }
  }

  const digits = (v: string, max: number) => v.replace(/\D/g, '').slice(0, max);

  return (
    <PessoasModal open={open} size="lg" title="Nova conta bancária" submitLabel="Cadastrar" isSubmitting={criar.isPending}
      submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AutocompleteSelect
          label="Banco"
          options={(bancos.data ?? []).map((b) => ({ value: b.code, label: `${b.code} — ${b.name}` }))}
          value={value.bankCode}
          onChange={(v) => setValue((x) => ({ ...x, bankCode: v }))}
        />
        <AutocompleteSelect
          label="Tipo de conta"
          options={ACCOUNT_TYPE_OPTIONS}
          value={value.accountType}
          onChange={(v) => setValue((x) => ({ ...x, accountType: v as BankAccountPayload['accountType'] }))}
        />
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2"><TextInput label="Agência" value={value.agency} onChange={(e) => setValue((x) => ({ ...x, agency: digits(e.target.value, 10) }))} /></div>
          <TextInput label="Dígito" value={value.agencyDigit ?? ''} onChange={(e) => setValue((x) => ({ ...x, agencyDigit: e.target.value.slice(0, 2) }))} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2"><TextInput label="Conta" value={value.account} onChange={(e) => setValue((x) => ({ ...x, account: digits(e.target.value, 20) }))} /></div>
          <TextInput label="Dígito" value={value.accountDigit ?? ''} onChange={(e) => setValue((x) => ({ ...x, accountDigit: e.target.value.slice(0, 2) }))} />
        </div>
        <TextInput label="Chave PIX (opcional)" value={value.pixKey ?? ''} onChange={(e) => setValue((x) => ({ ...x, pixKey: e.target.value }))} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={outroTitular} onChange={(e) => setOutroTitular(e.target.checked)} />
        A conta é de outro titular
      </label>
      {outroTitular && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextInput label="Nome do titular" value={value.holderName ?? ''} onChange={(e) => setValue((x) => ({ ...x, holderName: e.target.value }))} />
          <TextInput label="CPF do titular" value={value.holderCpf ?? ''} onChange={(e) => setValue((x) => ({ ...x, holderCpf: maskCPF(e.target.value) }))} />
        </div>
      )}
    </PessoasModal>
  );
}
